import { effectiveUserRoles, type UserRole } from "@the-drop/types";

const DISPENSARY_STAFF: UserRole[] = ["dispensary_dispatcher", "dispensary_budtender"];

/** Who may open the workspace at all (§15.2 ops surface). */
export function isDispensaryOpsRole(role: UserRole): boolean {
  return role === "platform_admin" || DISPENSARY_STAFF.includes(role);
}

/**
 * Multi-role: access if **any** effective role may use the dispensary ops shell (not only legacy **`role`**).
 */
export function isDispensaryOpsUser(user: { role: UserRole; roles?: UserRole[] }): boolean {
  return effectiveUserRoles(user).some((r) => isDispensaryOpsRole(r));
}

/**
 * §15.2 “manager” screens (profile, invitations, bank) — API uses **`dispensary_budtender`** for broader
 * store duties until a dedicated manager role exists (**`dispensary_dispatcher`** = queue-only).
 */
export function canAccessDispensaryManagerScreens(role: UserRole): boolean {
  return role === "platform_admin" || role === "dispensary_budtender";
}

export function canAccessDispensaryManagerScreensUser(user: {
  role: UserRole;
  roles?: UserRole[];
}): boolean {
  return effectiveUserRoles(user).some((r) => canAccessDispensaryManagerScreens(r));
}

export function resolveDispensaryIdForWorkspace(
  role: UserRole,
  userDispensaryId: string | undefined,
  adminScopeDispensaryId: string | null,
): string | null {
  if (DISPENSARY_STAFF.includes(role)) {
    return userDispensaryId?.trim() || null;
  }
  if (role === "platform_admin") {
    const s = adminScopeDispensaryId?.trim();
    return s && s.length > 0 ? s : null;
  }
  return null;
}

/**
 * Resolves workspace dispensary id using **all** roles from **`/me`**: **`platform_admin`** uses session scope;
 * dispensary staff roles use **`user.dispensaryId`**.
 */
export function resolveDispensaryIdForWorkspaceUser(
  user: { role: UserRole; roles?: UserRole[]; dispensaryId?: string },
  selectedScopeDispensaryId: string | null,
): string | null {
  const rs = effectiveUserRoles(user);
  if (rs.includes("platform_admin")) {
    const s = selectedScopeDispensaryId?.trim();
    return s && s.length > 0 ? s : null;
  }
  if (rs.some((r) => DISPENSARY_STAFF.includes(r))) {
    // Single-location staff are pinned to their one store.
    const own = user.dispensaryId?.trim();
    if (own && own.length > 0) return own;
    // Org-scoped staff (multi-location: no single `dispensaryId`) operate the
    // location they pick; the selection is persisted as the scope and validated
    // against their memberships by the switcher (and by the API).
    const s = selectedScopeDispensaryId?.trim();
    return s && s.length > 0 ? s : null;
  }
  return null;
}
